import { useMemo, useState } from 'react'
import { Filter, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { DagFilterExpressionBuilder } from '@/components/PlanDetail/DagFilterExpressionBuilder'
import {
  EMPTY_FILTER_GROUP,
  countConditions,
  serializeDagFilterGroup,
} from '@/components/PlanDetail/dagFilterUtils'
import type { FilterGroup } from '@/types'
import {
  SKU_FILTER_FIELDS,
  SKU_FILTER_ORDER,
  skuFilterDraft,
} from './skuFilters'

interface Props {
  filter: FilterGroup | null
  onApplyFilter: (filter: FilterGroup | null) => void
}

/**
 * "Filters" for the SKU grid: the AP line items' Filters button and rule
 * builder (same component, layout and wording), over SKU fields. Edits stay in
 * a draft until Apply.
 */
export function SkuFilterButton({ filter, onApplyFilter }: Props) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<FilterGroup>(() =>
    filter ? skuFilterDraft(filter) : EMPTY_FILTER_GROUP
  )
  const activeCount = useMemo(() => countConditions(filter), [filter])

  const apply = () => {
    const serialized = serializeDagFilterGroup(draft, SKU_FILTER_FIELDS)
    onApplyFilter(countConditions(serialized) > 0 ? serialized : null)
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        if (next) setDraft(filter ? skuFilterDraft(filter) : EMPTY_FILTER_GROUP)
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="cursor-pointer">
          <Filter size={14} /> Filters
          {activeCount > 0 && (
            <Badge className="h-5 min-w-5 justify-center px-1.5 py-0">
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={8}
        className="flex w-[640px] max-w-[calc(100vw-16px)] max-h-[min(38rem,var(--radix-popover-content-available-height,38rem))] flex-col p-3"
      >
        <div className="mb-2 flex shrink-0 items-center">
          <span className="flex items-center gap-1.5 text-sm font-semibold">
            <Filter className="h-3.5 w-3.5" />
            Filter Options
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <DagFilterExpressionBuilder
            group={draft}
            onChange={setDraft}
            fieldMeta={SKU_FILTER_FIELDS}
            fieldOrder={SKU_FILTER_ORDER}
          />
        </div>
        <div className="mt-3 flex shrink-0 justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer gap-1"
            onClick={() => {
              setDraft(EMPTY_FILTER_GROUP)
              onApplyFilter(null)
              setOpen(false)
            }}
            disabled={activeCount === 0}
          >
            <X className="h-3.5 w-3.5" />
            Clear Filters
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="cursor-pointer"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="cursor-pointer"
              onClick={apply}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
